import { Controller } from "@hotwired/stimulus"

// Autocomplete for the GridSignal scan form. Shows BOTH cities and
// counties — when a name matches both (e.g. "Hamilton"), the user
// sees both grouped under "City" and "County" headers with a type
// badge, matching how Market Finder's main search surfaces MSA /
// City / Zip. The user explicitly picks which to scan.
//
// On select: populates the hidden city, state, and jurisdiction_type
// inputs and enables the Submit button. The user never types the
// state separately; picking a geography locks in both.
export default class ScanPickerController extends Controller {
  static targets = ["input", "results", "cityInput", "stateInput", "typeInput", "submit", "selected"]

  connect() {
    this._timer = null
    this._hideResults = this.hideResults.bind(this)
    document.addEventListener("click", this._hideResults)
    this.disableSubmit()
  }

  disconnect() {
    document.removeEventListener("click", this._hideResults)
  }

  onInput() {
    clearTimeout(this._timer)
    this.clearSelection()
    const q = this.inputTarget.value.trim()
    if (q.length < 2) {
      this.hideResults()
      return
    }
    this._timer = setTimeout(() => this.search(q), 250)
  }

  async search(q) {
    try {
      // Ask the API for cities AND counties. Without the types
      // filter, broad matches like "Memphis" would pull the Memphis
      // MSA plus hundreds of ZIP children, pushing the real rows
      // past the 50-row cap. With `types=city,county`, the API skips
      // MSA/ZIP child lookups and returns a clean city+county list.
      const resp = await fetch(`/api/geographies/search?q=${encodeURIComponent(q)}&types=city,county`, {
        headers: { "Accept": "application/json" }
      })
      const results = await resp.json()
      const relevant = results.filter(r => r.geo_type === "city" || r.geo_type === "county")
      this.renderResults(relevant)
    } catch (_e) {
      this.hideResults()
    }
  }

  renderResults(rows) {
    if (!rows.length) {
      this.resultsTarget.innerHTML = `<div class="search-result-item no-results">No match. Try the full city or county name (e.g. "Hamilton" or "Hamilton County").</div>`
      this.resultsTarget.style.display = "block"
      return
    }

    // Group by type like Market Finder's main search (MSA / City /
    // Zip header + geo-type badge). When "Hamilton" matches both a
    // city and a county, user sees both and picks which to scan.
    const cities   = rows.filter(r => r.geo_type === "city").slice(0, 8)
    const counties = rows.filter(r => r.geo_type === "county").slice(0, 8)

    let html = ""
    if (cities.length) {
      html += '<div class="search-group-header">City</div>'
      html += cities.map(r => this.rowHtml(r, "city")).join("")
    }
    if (counties.length) {
      html += '<div class="search-group-header">County</div>'
      html += counties.map(r => this.rowHtml(r, "county")).join("")
    }

    this.resultsTarget.innerHTML = html
    this.resultsTarget.style.display = "block"
  }

  rowHtml(row, type) {
    const label = row.display_name ||
      (type === "county"
        ? `${row.city} County, ${row.state}`
        : `${row.city}, ${row.state}`)
    const badge      = type === "county" ? "County" : "City"
    const badgeClass = type === "county" ? "geo-type-county" : "geo-type-city"

    return `
      <div class="search-result-item"
           data-action="click->scan-picker#select"
           data-city="${this.escape(row.city || "")}"
           data-state="${this.escape(row.state || "")}"
           data-type="${type}"
           data-display="${this.escape(label)}">
        <span class="geo-type-badge ${badgeClass}">${badge}</span>
        ${this.escape(label)}
      </div>
    `
  }

  select(event) {
    const btn = event.currentTarget
    const city = btn.dataset.city
    const state = btn.dataset.state
    const type = btn.dataset.type || "city"
    const display = btn.dataset.display
    this.cityInputTarget.value = city
    this.stateInputTarget.value = state
    if (this.hasTypeInputTarget) this.typeInputTarget.value = type
    this.inputTarget.value = display
    this.hideResults()
    this.enableSubmit()
    if (this.hasSelectedTarget) {
      this.selectedTarget.textContent = `Selected: ${display}`
      this.selectedTarget.style.display = ""
    }
  }

  clearSelection() {
    this.cityInputTarget.value = ""
    this.stateInputTarget.value = ""
    if (this.hasTypeInputTarget) this.typeInputTarget.value = ""
    this.disableSubmit()
    if (this.hasSelectedTarget) this.selectedTarget.style.display = "none"
  }

  hideResults(event) {
    if (event && this.element.contains(event.target)) return
    this.resultsTarget.style.display = "none"
  }

  enableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove("btn-disabled")
    }
  }

  disableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("btn-disabled")
    }
  }

  escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
